import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import axios from 'axios';

import { createUseStyles } from 'react-jss';
import { TextField } from '@mui/material';
import { Assignment, Search } from '@mui/icons-material';
import { Permissions } from '../../config';
import { specialUserApi, fetcher } from 'lib/api';
import api from 'lib/api';
import useSWR, { mutate } from 'swr';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Table from 'components/Table/Table.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import styles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';
import customSelectStyle from 'assets/jss/material-dashboard-pro-react/customSelectStyle.js';
import customCheckboxRadioSwitch from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch.js';

import AddSpecialUser from './AddSpecialUser';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import MembershipPhotoEditDialog, {
    MembershipPhotoThumb,
} from 'components/MembershipPhotoUpload/MembershipPhotoUpload';
import MembershipPhotoBulkUpload from 'components/MembershipPhotoUpload/MembershipPhotoBulkUpload';

import Autocomplete from '@mui/material/Autocomplete';
import {
    Avatar,
    Chip,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    CircularProgress,
    Checkbox,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Divider,
    Collapse,
    Fade,
    Slide,
} from '@mui/material';
import { MoreVert, Edit, Delete, Checkbox as CheckboxIcon } from '@mui/icons-material';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { formatKennitala } from '../../utils/kennitalaFormatter';
import formatWithThousandSeparator from '../../utils/thousand_separator';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaFilter from 'components/SparaFilter/SparaFilter';
import HoverInfoBox from 'components/HoverInfoBox/HoverInfoBox';
import { FlexContainer } from 'components/FlexContainer/FlexContainer';

// Bæta við Spara litum
const SPARA_RED = 'var(--spara-primary, #DE2C48)';
const LIGHT_GRAY = 'var(--spara-text-muted, #C0C0C0)';

// Bæta við global stílum fyrir MuiTableCell
const globalStyles = {
    '@global': {
        '.MuiTableCell-body': {
            padding: '2px 12px !important',
        },
        '.MuiTableRow-root': {
            height: 'unset !important',
        },
    },
};

const stylesAdd = {
    txtArea: {
        height: '84px',
        width: '100%',
        borderColor: 'rgb(210, 210, 210)',
        fontSize: '14px',
        fontFamily: 'Roboto, Helvetica, Arial',
        fontWeight: 400,
        lineHeight: 1.42857,
        resize: 'none',
    },
    txtAreaError: {
        borderColor: '#DE2C48',
    },
    txtAreaSuccess: {
        borderColor: '#6bbd6e',
    },
    txtSuccess: {
        color: '#6bbd6e',
        fontWeight: 'bold',
    },
    choiche: {
        position: 'relative',
        float: 'left',
        textAlign: 'center',
        cursor: 'pointer',
        // marginTop: "20px"
        width: '60px',
        margin: '5px 0',
    },
    iconCheckbox: {
        width: '50px',
        height: '50px',
    },
    logoImg: {
        width: '90%',
        height: '90%',
        opacity: '0.5',
        borderRadius: '50%',
    },
    companies: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    error: {
        textAlign: 'center',
        color: '#C42431',
        fontWeight: 'bold',
    },
    iconSelected: {
        backgroundColor: '#fff',
        border: '1px solid #C42431',
        boxShadow: '0 1px 3px rgba(196, 36, 49, 0.1)',
        '& .companyName': {
            color: '#C42431',
            fontWeight: 600,
        },
        '& .logoImg': {
            boxShadow: '0 1px 2px rgba(196, 36, 49, 0.15)',
            opacity: 1,
        },
        '& .MuiCheckbox-root .MuiSvgIcon-root': {
            color: '#C42431',
            transform: 'scale(1.05)',
        },
        '&:hover': {
            backgroundColor: 'rgba(196, 36, 49, 0.04)',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 4px rgba(196, 36, 49, 0.15)',
        },
    },
    ...customSelectStyle,
    ...customCheckboxRadioSwitch,
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#333',
        marginBottom: '0.25rem',
        marginTop: '1rem',
        textAlign: 'left',
        paddingLeft: '0.25rem',
    },
    sectionDescription: {
        fontSize: '0.85rem',
        color: '#666',
        marginBottom: '0.75rem',
        lineHeight: '1.3',
        textAlign: 'left',
        paddingLeft: '0.25rem',
    },
    companiesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '0.5rem',
        marginTop: '0.25rem',
        textAlign: 'left',
        padding: '0 0.25rem',
    },
    companyItem: {
        position: 'relative',
        width: '100%',
        textAlign: 'left',
    },
    companyCheckbox: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.5rem',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        backgroundColor: '#fff',
        textAlign: 'left',
        justifyContent: 'flex-start',
        minHeight: '40px',
        '&:hover': {
            backgroundColor: 'rgba(196, 36, 49, 0.04)',
            borderColor: '#C42431',
            transform: 'translateY(-1px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        },
        '& .MuiCheckbox-root': {
            padding: '2px',
            marginRight: '6px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            '& .MuiSvgIcon-root': {
                fontSize: '18px',
                color: '#666',
                transition: 'all 0.2s ease-in-out',
            },
            '&:hover .MuiSvgIcon-root': {
                color: '#C42431',
            },
            '&.Mui-checked': {
                '& .MuiSvgIcon-root': {
                    color: '#C42431',
                },
            },
        },
    },
    logoImg: {
        width: '20px',
        height: '20px',
        borderRadius: '3px',
        objectFit: 'cover',
        marginRight: '0.5rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        flexShrink: 0,
        alignSelf: 'center',
    },
    companyName: {
        fontSize: '0.85rem',
        color: '#333',
        fontWeight: 500,
        flex: 1,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'all 0.2s ease-in-out',
        textAlign: 'left',
        lineHeight: '1.2',
        minHeight: '2.4em',
        display: 'flex',
        alignItems: 'center',
    },
    singleCompanyInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginTop: '1rem',
    },
    singleCompanyHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        width: '100%',
        '& img': {
            width: '48px',
            height: '48px',
            borderRadius: '8px',
            objectFit: 'cover',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        '& h4': {
            margin: 0,
            fontSize: '1.1rem',
            color: '#333',
            fontWeight: 500,
        },
    },
};

const style = {
    choiche: {
        position: 'relative',
        float: 'left',
        textAlign: 'center',
        cursor: 'pointer',
        width: '45px',
    },
    iconCheckbox: {
        width: '40px',
        height: '40px',
    },
    logoImg: {
        width: '90%',
        height: '90%',
        opacity: '0.5',
        borderRadius: '50%',
    },
    ...styles,
    ...customSelectStyle,
    ...customCheckboxRadioSwitch,
};

const useStyles = createUseStyles({ ...style, ...globalStyles });
const useStylesAdd = createUseStyles({ ...stylesAdd, ...globalStyles });

const generateColorFromId = (id, kennitala) => {
    if (!id && !kennitala) return 'var(--spara-surface-2, #cccccc)';

    // Use kennitala if available, otherwise use ID
    const input = kennitala || id.toString();

    // Create a more complex hash that ensures sequential inputs have very different colors
    const hash = input.split('').reduce((acc, char, index) => {
        const charCode = char.charCodeAt(0);
        // Multiply by prime numbers and add position to create more variation
        return (acc * 31 + charCode * (index + 1) * 17) % 360;
    }, 0);

    return `hsl(${hash}, 55%, var(--spara-avatar-bg-l, 88%))`;
};

const generateTextColorFromId = (id, kennitala) => {
    if (!id && !kennitala) return 'var(--spara-text-muted, #666666)';

    // Use kennitala if available, otherwise use ID
    const input = kennitala || id.toString();

    // Create a more complex hash that ensures sequential inputs have very different colors
    const hash = input.split('').reduce((acc, char, index) => {
        const charCode = char.charCodeAt(0);
        // Multiply by prime numbers and add position to create more variation
        return (acc * 31 + charCode * (index + 1) * 17) % 360;
    }, 0);

    return `hsl(${hash}, 55%, var(--spara-avatar-fg-l, 30%))`;
};

const SpecialUsers = ({ t }) => {
    const classes = useStyles();
    const classes2 = useStylesAdd();
    const [alert, setAlert] = useState(null);
    const [notifDialog, setNotifDialog] = useState({
        open: false,
        type: 'success',
        title: '',
        message: '',
    });
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [batchIds, setBatchIds] = useState([]);
    const [totalBatchJobs, setTotalBatchJobs] = useState(0);
    const [importProgress, setImportProgress] = useState(0);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    const [specialUsersState, setSpecialUsersState] = useState([]);
    const userStorage = JSON.parse(localStorage.getItem('user')) || {
        permission: [],
        roles: [],
        user: { torgid_company_id: null },
    };
    const userPermission = userStorage.permission || [];
    const canUpdate = userPermission.includes(Permissions.SPECIAL_USER_UPDATE);
    const canDelete = userPermission.includes(Permissions.SPECIAL_USER_DELETE);
    const companyIdStorage = userStorage.user.torgid_company_id;

    // Athugum hvort notandi sé super_admin
    const isSuperAdmin = Array.isArray(userStorage.roles)
        ? userStorage.roles.includes('super_admin')
        : false;

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageLimit] = useState(100);
    const [isActiveLoadMore, setIsActiveLoadMore] = useState(true);
    const [showGroupSelector, setShowGroupSelector] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [notifyAppUsersOnGroupUpdate, setNotifyAppUsersOnGroupUpdate] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [filterGroups, setFilterGroups] = useState([]);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [allPages, setAllPages] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const fetchTimeoutRef = useRef(null);
    const [totalCount, setTotalCount] = useState(0);
    const [lastFetchedPage, setLastFetchedPage] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedGroupsForDelete, setSelectedGroupsForDelete] = useState([]);
    const [pendingJobs, setPendingJobs] = useState(0);
    const [photoMember, setPhotoMember] = useState(null);
    const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);

    // Ef notandi er super_admin förum við ekki með my-groups=true til að sækja alla hópa
    const specialCompaniesUrl = isSuperAdmin
        ? '/special_company'
        : '/special_company?my-groups=true';

    const { data: specialCompaniesData, isLoading: isLoadingCompanies } = useSWR(
        specialCompaniesUrl,
        fetcher
    );

    const addSpecialUserLabel = useMemo(() => {
        const groupCount = specialCompaniesData?.results?.length || 0;
        return t(groupCount > 1 ? 'add_new_special_user_plural' : 'add_new_special_user');
    }, [specialCompaniesData?.results?.length, t]);

    const photoEnabledGroups = useMemo(() => {
        const companies = specialCompaniesData?.results || [];
        const filteredIds = Object.values(filters)
            .flat()
            .map((id) => Number(id))
            .filter(Boolean);
        const relevant =
            filteredIds.length > 0
                ? companies.filter((c) => filteredIds.includes(Number(c.id)))
                : companies;
        return relevant.filter((c) => !!c.allow_member_photo);
    }, [specialCompaniesData?.results, filters]);

    const showMembershipPhotos = photoEnabledGroups.length > 0;

    const { data: specialUsersData, error, isLoading: isLoadingUsers, isMutating } = useSWR(
        specialUserApi.list({
            page,
            limit: pageLimit,
            search: debouncedSearchValue,
            group_ids: Object.values(filters).flat(),
        }),
        fetcher,
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
        }
    );

    // Sérstakur fetcher sem styður marga batcha í einu með nýja endpoint
    const batchFetcher = async (...args) => {
        console.log('batchFetcher called with args:', args);

        // useSWR sendir url sem fyrsta parameter og önnur sem args
        let batchIds;
        if (Array.isArray(args[0])) {
            // Ef fyrsti parameter er array, þá er batchIds í args0
            batchIds = args[0][1];
        } else {
            // Ef fyrsti parameter er string, þá er batchIds í args[1]
            batchIds = args[1];
        }

        console.log('Extracted batchIds:', batchIds);
        if (!batchIds || batchIds.length === 0) {
            console.log('No batchIds, returning empty array');
            return [];
        }

        try {
            console.log('Making POST request to /special_user/batch/multiple with:', {
                batch_ids: batchIds,
            });
            const response = await fetcher('/special_user/batch/multiple', 'POST', {
                batch_ids: batchIds,
            });

            console.log('Response from batch/multiple:', response);
            // Skila batches array úr svari
            return response?.results?.batches || [];
        } catch (error) {
            console.error('Error fetching batch status:', error);
            return [];
        }
    };

    const { data: batchesData } = useSWR(
        batchIds.length ? ['batch-progress', batchIds] : null,
        batchFetcher,
        {
            refreshInterval: 2000,
            dedupingInterval: 1500,
            onSuccess: async () => {
                // Þetta callback keyrir þegar fetch er completed (fyrir alla batcha í Promise.all).
                // Framvindan reiknuð í useEffect hér að neðan.
            },
        }
    );

    useEffect(() => {
        if (!batchesData || batchesData.length === 0) return;

        // Reikna heildarframvindu og stöðu
        let completedSum = 0;
        let totalSum = totalBatchJobs;
        let anyFailed = false;
        let allFinished = true;
        let pendingJobCount = 0;

        batchesData.forEach((batch) => {
            const total = batch?.total_jobs || 0;
            const pending = batch?.pending_jobs || 0;
            const failed = batch?.failed_jobs || 0;
            const finished = batch?.status === 'finished' || pending === 0;

            completedSum += total - pending;
            if (totalSum === 0) totalSum += total; // fallback ef totalBatchJobs ekki stillt
            pendingJobCount += pending;

            if (failed > 0) anyFailed = true;
            if (!finished) allFinished = false;
        });

        const percent = totalSum > 0 ? Math.round((completedSum / totalSum) * 100) : 0;
        setPendingJobs(pendingJobCount);
        setImportProgress(percent);

        if (allFinished) {
            // Uppfæra gögnin með mutate
            mutate(
                specialUserApi.list({
                    page: 1,
                    limit: pageLimit,
                    search: debouncedSearchValue,
                    group_ids: Object.values(filters).flat(),
                })
            );
            setBatchIds([]);
            setIsImportModalOpen(false);
            showDialog(
                'success',
                t('success'),
                t('batch_import_completed', { defaultValue: 'Innflutningi lokið' })
            );
        } else if (anyFailed) {
            setBatchIds([]);
            setIsImportModalOpen(false);
            showDialog(
                'danger',
                t('error'),
                t('batch_import_failed', { defaultValue: 'Villa kom upp við innflutning' })
            );
        }
    }, [batchesData, debouncedSearchValue, filters, pageLimit, t, totalBatchJobs]);

    useEffect(() => {
        if (specialCompaniesData?.results?.length > 1) {
            setFilterGroups([
                specialCompaniesData.results.map((company) => ({
                    id: company.id,
                    label: company.name,
                    value: company.id,
                    icon: () => (
                        <Avatar
                            src={company.logo}
                            alt={company.name}
                            sx={{ width: 24, height: 24 }}
                        />
                    ),
                })),
            ]);
        } else {
            setFilterGroups([]);
        }
    }, [specialCompaniesData?.results, t]);

    useEffect(() => {
        if (specialUsersData?.results) {
            // Ef þetta er fyrsta síða eða við erum að leita, hreinsa út gömlu niðurstöðurnar
            if (page === 1 && debouncedSearchValue !== '') {
                setAllPages(specialUsersData.results);
            } else if (page === 1) {
                // Ef við erum að hreinsa leit, bíða þar til ný gögn eru tilbúin
                if (!isMutating) {
                    setAllPages(specialUsersData.results);
                }
            } else {
                setAllPages((prev) => {
                    // Búa til Map með núverandi notendum
                    const uniqueUsers = new Map(prev.map((user) => [user.id, user]));
                    // Bæta við nýjum notendum, en bara ef þeir eru ekki þegar til
                    specialUsersData.results.forEach((user) => {
                        if (!uniqueUsers.has(user.id)) {
                            uniqueUsers.set(user.id, user);
                        }
                    });
                    // Breyta Map aftur í array
                    return Array.from(uniqueUsers.values());
                });
            }
            setTotalCount(specialUsersData.count || 0);
            setLastFetchedPage(page);
            setSearchLoading(false);
            setIsLoadingMore(false);
            setIsFetching(false);
            setHasMore(specialUsersData.results.length === pageLimit);
        }
    }, [
        specialUsersData?.results,
        page,
        pageLimit,
        lastFetchedPage,
        debouncedSearchValue,
        isMutating,
        specialUsersData?.count,
    ]);

    const onSubmit = async (data) => {
        let temp = data.users.replace(/^\s+|\s+$/g, '').split(/\s+/);
        data.users = temp.join(' ');
        data.groups = (data.companies || []).join(',');
        try {
            const res = await fetcher(specialUserApi.create(), 'POST', data);
            let newBatchIds = [];
            if (res?.batch_id) newBatchIds = [res.batch_id];
            else if (res?.results?.batch_id) newBatchIds = [res.results.batch_id];
            else if (res?.results?.batch_ids) newBatchIds = res.results.batch_ids;
            else if (res?.results?.id) newBatchIds = [res.results.id];

            if (newBatchIds.length) {
                setBatchIds((prev) => [...prev, ...newBatchIds]);
                setTotalBatchJobs(res?.results?.total_jobs || 0);
                setImportProgress(0);
                setIsImportModalOpen(true);
            } else {
                showDialog(
                    'success',
                    t('success'),
                    t('special_users_added', { defaultValue: 'Félögum bætt við' })
                );
            }
        } catch (error) {
            console.error('Villa við að bæta félögum við:', error);
            showDialog(
                'danger',
                t('error'),
                error?.response?.data?.message || t('error_adding_special_users')
            );
        }
    };

    const hideAlert = () => {
        setAlert(null);
    };

    const showDialog = (type, title, message) => {
        setNotifDialog({ open: true, type, title, message });
    };

    const handleFilterClick = (filterId, value) => {
        const newFilters = { ...filters };

        if (!newFilters[filterId]) {
            newFilters[filterId] = [value];
        } else {
            const values = newFilters[filterId];
            if (values.includes(value)) {
                const index = values.indexOf(value);
                values.splice(index, 1);
                if (values.length === 0) {
                    delete newFilters[filterId];
                }
            } else {
                values.push(value);
            }
        }

        // Hreinsa út allar síður og endurstilla state
        setFilters(newFilters);
        setPage(1);
        setLastFetchedPage(0);
        setAllPages([]);
        setTotalCount(0);
        setIsActiveLoadMore(true);
        setSearchLoading(true);
        setIsFetching(false);
        // Hreinsa út valda notendur
        setSelectedUsers(new Set());
        setSelectAll(false);
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        mutate(
            specialUserApi.list({
                page: 1,
                limit: pageLimit,
                search,
                group_ids: Object.values(newFilters).flat(),
            })
        );
    };

    const handleClearAll = () => {
        // Hreinsa út allar síur og leitarorð
        setFilters({});
        setSearch('');
        setDebouncedSearchValue(''); // Bæta við þessu til að endurstilla debounced gildið
        setPage(1);
        setLastFetchedPage(0);
        // Ekki hreinsa út listann strax, bíða þar til ný gögn eru tilbúin
        setTotalCount(0);
        setIsActiveLoadMore(true);
        setIsFetching(false);
        setSelectedUsers(new Set()); // Hreinsa út valda notendur
        setSelectAll(false); // Endurstilla "velja allt" state
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        // Endurhlaða gögnum með hreinu state
        mutate(
            specialUserApi.list({
                page: 1,
                limit: pageLimit,
            })
        );
    };

    const handleSearchInputChange = (event) => {
        const value = event.target.value;
        setSearch(value);

        // Ef leitarsvæðið er tómt, hreinsa strax debounced gildið
        if (value === '') {
            setDebouncedSearchValue('');
            // Hreinsa út valda notendur þegar leit er hreinsuð
            setSelectedUsers(new Set());
            setSelectAll(false);
        } else {
            // Hreinsa ekki út listann fyrr en ný gögn eru tilbúin
            debouncedSearch(value);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value) => {
            setPage(1);
            setIsActiveLoadMore(true);
            setSearchLoading(true);
            // Hreinsa út valda notendur þegar ný leit er framkvæmd
            setSelectedUsers(new Set());
            setSelectAll(false);
            if (value.length > 2) {
                setDebouncedSearchValue(value);
            } else if (value.length === 0) {
                setDebouncedSearchValue('');
            }
        }, 250),
        [pageLimit, filters]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const htmlAlert = () => {
        setAlert(
            <AddSpecialUser
                classesAdd={classes2}
                companies={specialCompaniesData?.results}
                onCancel={() => hideAlert()}
                onSubmit={(data, update) => onSubmit(data, update)}
            />
        );
    };

    const handleMenuClick = (event, user) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedUser(null);
    };

    const handleUpdateGroups = (user) => {
        handleMenuClose();
        setSelectedUser(user);
        setNotifyAppUsersOnGroupUpdate(false);
        setShowGroupSelector(true);

        // Tryggja að torgid_special_companies sé alltaf array
        const userCompanies = user.torgid_special_companies
            ? Array.isArray(user.torgid_special_companies)
                ? user.torgid_special_companies
                : user.torgid_special_companies.split(',').map(Number)
            : [];

        // Finna hópa sem notandi hefur raunverulega aðgang að
        const availableGroups =
            specialCompaniesData?.results?.filter((company) =>
                userCompanies.includes(company.id)
            ) || [];

        setSelectedGroups(availableGroups);
    };

    const handleGroupChange = (event, newValue) => {
        setSelectedGroups(newValue);
    };

    const handleSaveGroups = async () => {
        if (selectedUser && selectedGroups) {
            const companies = selectedGroups.map((company) => company.id);
            try {
                await api.put(specialUserApi.update(selectedUser.id), {
                    user_companies: companies.join(','),
                    notify_app_users: notifyAppUsersOnGroupUpdate,
                });
                // Uppfæra gögnin með mutate
                mutate(
                    specialUserApi.list({
                        page: 1,
                        limit: pageLimit,
                        search: debouncedSearchValue,
                        group_ids: Object.values(filters).flat(),
                    })
                );
                setShowGroupSelector(false);
                setNotifyAppUsersOnGroupUpdate(false);
                setAllPages((prev) =>
                    prev.map((user) =>
                        user.id === selectedUser.id
                            ? { ...user, torgid_special_companies: companies }
                            : user
                    )
                );
            } catch (error) {
                console.error('Villa við að vista hópa:', error);
                showDialog('danger', t('error'), t('error_updating_groups'));
            }
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            await fetcher(specialUserApi.delete(userId), 'DELETE');
            setAllPages((prev) => prev.filter((user) => user.id !== userId));
            setTotalCount((prev) => prev - 1);
            handleMenuClose();
        } catch (error) {
            console.error('Villa við að eyða notanda:', error);
            showDialog(
                'danger',
                t('error'),
                error?.response?.data?.message || t('error_deleting_user')
            );
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers((prev) => {
            const newSelected = new Set(prev);
            if (newSelected.has(userId)) {
                newSelected.delete(userId);
            } else {
                newSelected.add(userId);
            }
            return newSelected;
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(allPages.map((user) => user.id)));
        }
        setSelectAll(!selectAll);
    };

    const handleGroupToggle = (group) => {
        setSelectedGroupsForDelete((prev) => {
            const isSelected = prev.some((g) => g.id === group.id);
            if (isSelected) {
                return prev.filter((g) => g.id !== group.id);
            } else {
                return [...prev, group];
            }
        });
    };

    const handleDeleteFromGroups = async () => {
        if (selectedUsers.size === 0 || selectedGroupsForDelete.length === 0) return;

        try {
            console.log('Sending delete request with:', {
                ids: Array.from(selectedUsers),
                group_ids: selectedGroupsForDelete.map((group) => group.id),
            });

            const response = await api.post(specialUserApi.deleteByIds(), {
                ids: Array.from(selectedUsers),
                group_ids: selectedGroupsForDelete.map((group) => group.id),
            });

            console.log('Delete response:', response);

            if (response.data) {
                // Uppfæra gögnin
                await mutate(
                    specialUserApi.list({
                        page: 1,
                        limit: pageLimit,
                        search: debouncedSearchValue,
                        group_ids: Object.values(filters).flat(),
                    })
                );

                // Hreinsa út valda notendur og hópa
                setSelectedUsers(new Set());
                setSelectAll(false);
                setSelectedGroupsForDelete([]);
                setShowDeleteDialog(false);

                showDialog(
                    'success',
                    t('success'),
                    t('members_removed_from_group', {
                        defaultValue: 'Félagar fjarlægðir úr hópnum',
                    })
                );
            }
        } catch (error) {
            console.error('Villa við að eyða notendum úr hópum:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            showDialog(
                'danger',
                t('error'),
                error.response?.data?.message || t('error_deleting_from_groups')
            );
        }
    };

    const getGroupsForSelectedUsers = useMemo(() => {
        if (!specialCompaniesData?.results || selectedUsers.size === 0) return [];

        // Safna öllum hópum sem valdir notendur eru í
        const userGroups = new Set();
        allPages.forEach((user) => {
            if (selectedUsers.has(user.id)) {
                const userCompanies = user.torgid_special_companies
                    ? Array.isArray(user.torgid_special_companies)
                        ? user.torgid_special_companies
                        : user.torgid_special_companies.split(',').map(Number)
                    : [];
                userCompanies.forEach((companyId) => userGroups.add(companyId));
            }
        });

        // Finna hópana sem valdir notendur eru í
        return specialCompaniesData.results.filter((company) => userGroups.has(company.id));
    }, [selectedUsers, specialCompaniesData?.results, allPages]);

    // Velja sjálfkrafa hópinn ef aðeins einn er til staðar
    useEffect(() => {
        if (getGroupsForSelectedUsers.length === 1) {
            setSelectedGroupsForDelete([getGroupsForSelectedUsers[0]]);
        } else {
            setSelectedGroupsForDelete([]);
        }
    }, [getGroupsForSelectedUsers]);

    const getTableHead = () => {
        const baseColumns = [
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    size="small"
                    inputProps={{
                        'aria-label': t('select_all_users'),
                        'aria-describedby': 'select-all-description',
                    }}
                    sx={{
                        mr: 0.5,
                        padding: '2px',
                        color: LIGHT_GRAY,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&.Mui-checked': {
                            color: SPARA_RED,
                            transform: 'scale(1.05)',
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(196, 36, 49, 0.04)',
                            transform: 'scale(1.05)',
                        },
                    }}
                />
                <Box>
                    <Typography component="span" variant="body1">
                        {t('special_users_member_column')}
                    </Typography>
                    <Typography
                        id="select-all-description"
                        component="span"
                        variant="caption"
                        sx={{
                            display: 'block',
                            color: 'text.secondary',
                            mt: 0.5,
                        }}
                    >
                        {t('select_all_description')}
                    </Typography>
                </Box>
            </Box>,
            t('kennitala'),
            ...(showMembershipPhotos ? [t('membership_photo')] : []),
            t('phone'),
            t('email'),
            t('last_updated'),
        ];

        // Bæta við hópum og aðgerðum dálkum ef það eru fleiri en einn hópur
        if (filterGroups?.[0]?.length !== 1) {
            baseColumns.push(t('menu.groups'), t('actions'));
        }

        return baseColumns;
    };

    const getConnectedUsersData = () => {
        if (!allPages || !specialCompaniesData?.results) return [[]];

        // Hjálparfall til að tryggja að companies sé alltaf array
        const ensureCompaniesArray = (companies) => {
            if (!companies) return [];
            if (Array.isArray(companies)) return companies;
            return companies.split(',').map(Number);
        };

        return allPages.map((value) => {
            const name = value.torgid_user ? value.torgid_user.name : '';
            const email = value.torgid_user ? value.torgid_user.email : value.email;
            const phone = value.torgid_user ? value.torgid_user.phone : value.phone;
            const kennitala = value.torgid_user ? value.torgid_user.kennitala : value.kennitala;
            const userId = value.torgid_user ? value.torgid_user.id : value.id;

            // Tryggja að torgid_special_companies sé alltaf array
            const spec_companies = ensureCompaniesArray(value.torgid_special_companies);

            // Finna hópa sem notandi hefur raunverulega aðgang að
            const userGroups = specialCompaniesData.results.filter((company) =>
                spec_companies.includes(company.id)
            );
            const memberCompanyIds = new Set(spec_companies.map(Number).filter(Boolean));
            const memberHasPhotoGroup = photoEnabledGroups.some((group) =>
                memberCompanyIds.has(Number(group.id))
            );

            const updatedAt = value.updated_at ? new Date(value.updated_at) : null;
            const isConnected = !!value.torgid_user;

            const baseCells = [
                <FlexContainer
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textAlign: 'left',
                        height: '42px',
                        minHeight: '42px',
                        minWidth: '3000px',
                    }}
                >
                    <Checkbox
                        checked={selectedUsers.has(value.id)}
                        onChange={() => handleSelectUser(value.id)}
                        size="small"
                        sx={{
                            mr: 0.5,
                            padding: '2px',
                            color: LIGHT_GRAY,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&.Mui-checked': {
                                color: SPARA_RED,
                                transform: 'scale(1.05)',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(196, 36, 49, 0.04)',
                                transform: 'scale(1.05)',
                            },
                        }}
                    />
                    {isConnected ? (
                        <>
                            <Avatar
                                style={{
                                    alignSelf: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleSelectUser(value.id)}
                                sx={{
                                    mr: 1,
                                    width: 32,
                                    height: 32,
                                    bgcolor: generateColorFromId(userId, kennitala),
                                    color: generateTextColorFromId(userId, kennitala),
                                    fontSize: '1rem',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    },
                                }}
                            >
                                {name ? name.charAt(0).toUpperCase() : '?'}
                            </Avatar>
                            <Typography
                                onClick={() => handleSelectUser(value.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    fontSize: '1rem',
                                    lineHeight: 1.3,
                                    m: 0,
                                    p: 0,
                                    cursor: 'pointer',
                                }}
                            >
                                {name || t('no_name')}
                            </Typography>
                        </>
                    ) : (
                        <FlexContainer
                            onClick={() => handleSelectUser(value.id)}
                            gap={5}
                            style={{ alignItems: 'center', flex: 1, cursor: 'pointer' }}
                        >
                            <Typography
                                style={{ margin: 0, padding: 0 }}
                                sx={{
                                    fontSize: '0.9rem',
                                    color: 'text.disabled',
                                    fontStyle: 'italic',
                                    lineHeight: 1,
                                    m: 0,
                                    p: 0,
                                }}
                            >
                                {t('user_not_connected')}
                            </Typography>
                            <HoverInfoBox color="var(--spara-text-muted, rgba(0, 0, 0, 0.38))">
                                {t('user_not_connected_info')}
                            </HoverInfoBox>
                        </FlexContainer>
                    )}
                </FlexContainer>,
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        color: kennitala ? 'inherit' : 'text.secondary',
                        fontSize: '1rem',
                        opacity: kennitala ? 1 : 0.5,
                        fontStyle: kennitala ? 'normal' : 'italic',
                        height: '42px',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1.3,
                        m: 0,
                        p: 0,
                        maxWidth: '7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {kennitala ? formatKennitala(kennitala) : isConnected ? t('no_kennitala') : ''}
                </Typography>,
                ...(showMembershipPhotos
                    ? [
                          <Box
                              key={`photo-${value.id}`}
                              sx={{
                                  height: '42px',
                                  minHeight: '42px',
                                  display: 'flex',
                                  alignItems: 'center',
                              }}
                          >
                              {memberHasPhotoGroup ? (
                                  <MembershipPhotoThumb
                                      member={value}
                                      groupId={
                                          filterGroups?.[0]?.length === 1
                                              ? filterGroups[0][0].value
                                              : undefined
                                      }
                                      disabled={!kennitala}
                                      onClick={() => {
                                          if (!kennitala) return;
                                          setPhotoMember(value);
                                      }}
                                  />
                              ) : null}
                          </Box>,
                      ]
                    : []),
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        color: phone ? 'inherit' : 'text.secondary',
                        fontSize: '1rem',
                        opacity: phone ? 1 : 0.5,
                        fontStyle: phone ? 'normal' : 'italic',
                        height: '42px',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1.3,
                        m: 0,
                        p: 0,
                        maxWidth: '5rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {phone ? formatPhoneNumber(phone) : isConnected ? t('no_phone') : ''}
                </Typography>,
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        color: email ? 'inherit' : 'text.secondary',
                        fontSize: '1rem',
                        opacity: email ? 1 : 0.5,
                        fontStyle: email ? 'normal' : 'italic',
                        height: '42px',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1.3,
                        m: 0,
                        p: 0,
                    }}
                >
                    {email || (isConnected ? t('no_email') : '')}
                </Typography>,
                <Typography
                    sx={{
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        height: '42px',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1.3,
                        m: 0,
                        p: 0,
                    }}
                >
                    {updatedAt
                        ? updatedAt.toLocaleDateString('is-IS', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                          })
                        : '-'}
                </Typography>,
            ];

            // Bæta við hópum og aðgerðum dálkum ef það eru fleiri en einn hópur
            if (filterGroups?.[0]?.length !== 1) {
                baseCells.push(
                    <Box
                        key={`groups-${value.id}`}
                        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                    >
                        {spec_companies
                            .sort((a, b) => a - b)
                            .map((companyId) => {
                                const company = specialCompaniesData.results.find(
                                    (c) => c.id === companyId
                                );
                                return company ? (
                                    <Chip
                                        key={company.id}
                                        label={company.name}
                                        size="small"
                                        avatar={<Avatar src={company.logo} alt={company.name} />}
                                    />
                                ) : (
                                    <Chip
                                        title={companyId}
                                        key={`unknown-${companyId}`}
                                        label={t('unknown_group', {
                                            defaultValue: 'Óþekktur hópur',
                                        })}
                                        size="small"
                                        sx={{
                                            '& .MuiChip-label': {
                                                color: 'text.disabled',
                                                fontStyle: 'italic',
                                            },
                                        }}
                                    />
                                );
                            })}
                    </Box>,
                    <Box>
                        <Tooltip title="Actions">
                            <IconButton onClick={(e) => handleMenuClick(e, value)}>
                                <MoreVert />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            }

            return baseCells;
        });
    };

    const handleLoadMore = () => {
        if (
            !isLoadingMore &&
            hasMore &&
            !isLoadingUsers &&
            !isLoadingCompanies &&
            !isMutating &&
            !isFetching
        ) {
            setIsFetching(true);
            setIsLoadingMore(true);
            setPage((prev) => prev + 1);
        }
    };
    console.log('filterGroups', filterGroups[0]);
    return (
        <GridContainer>
            {alert}
            <ConfirmDialog
                open={notifDialog.open}
                type={notifDialog.type}
                title={notifDialog.title}
                message={notifDialog.message}
                confirmText={t('ok', { defaultValue: 'Loka' })}
                onConfirm={() => setNotifDialog((prev) => ({ ...prev, open: false }))}
            />
            <MembershipPhotoEditDialog
                open={!!photoMember}
                member={photoMember}
                groups={photoEnabledGroups}
                onClose={() => setPhotoMember(null)}
                onSaved={() => {
                    mutate(
                        specialUserApi.list({
                            page,
                            limit: pageLimit,
                            search: debouncedSearchValue,
                            group_ids: Object.values(filters).flat(),
                        })
                    );
                }}
            />
            <MembershipPhotoBulkUpload
                open={bulkPhotoOpen}
                onClose={() => setBulkPhotoOpen(false)}
                companies={photoEnabledGroups}
                members={allPages}
                onDone={() => {
                    mutate(
                        specialUserApi.list({
                            page,
                            limit: pageLimit,
                            search: debouncedSearchValue,
                            group_ids: Object.values(filters).flat(),
                        })
                    );
                }}
            />
            {showGroupSelector && selectedUser && (
                <Dialog
                    open={showGroupSelector}
                    onClose={() => {
                        setShowGroupSelector(false);
                        setNotifyAppUsersOnGroupUpdate(false);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {t('update_groups')} - {selectedUser.torgid_user?.name || selectedUser.name}
                    </DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            multiple
                            value={selectedGroups}
                            onChange={handleGroupChange}
                            options={specialCompaniesData?.results || []}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Avatar
                                        src={option.logo}
                                        alt={option.name}
                                        sx={{ width: 24, height: 24, mr: 1 }}
                                    />
                                    {option.name}
                                </Box>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.name}
                                        {...getTagProps({ index })}
                                        avatar={<Avatar src={option.logo} alt={option.name} />}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label={t('menu.groups')}
                                    placeholder={t('select_groups')}
                                    style={{ marginTop: 10 }}
                                />
                            )}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                mt: 2,
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                setNotifyAppUsersOnGroupUpdate((prev) => !prev)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setNotifyAppUsersOnGroupUpdate((prev) => !prev);
                                }
                            }}
                        >
                            <Checkbox
                                checked={notifyAppUsersOnGroupUpdate}
                                onChange={() =>
                                    setNotifyAppUsersOnGroupUpdate((prev) => !prev)
                                }
                                onClick={(e) => e.stopPropagation()}
                            />
                            <Box>
                                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                                    {t('notify_app_users')}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: '#666', mt: 0.25 }}>
                                    {t('notify_app_users_description')}
                                </Typography>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setShowGroupSelector(false);
                                setNotifyAppUsersOnGroupUpdate(false);
                            }}
                        >
                            {t('cancel')}
                        </Button>
                        <Button color="success" variant="contained" onClick={handleSaveGroups}>
                            {t('save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {showDeleteDialog && (
                <Dialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {t('delete_from_groups', {
                            userText:
                                selectedUsers.size === 1
                                    ? t('member_singular')
                                    : t('member_plural'),
                            count: selectedUsers.size,
                            defaultValue: 'Fjarlægja {{count}} {{userText}} úr hópum',
                        })}
                    </DialogTitle>
                    <DialogContent>
                        {getGroupsForSelectedUsers.length > 1 ? (
                            <>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    {t('select_groups_to_delete_from')}
                                </Typography>

                                <List
                                    sx={{
                                        width: '100%',
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        maxHeight: 300,
                                        overflow: 'auto',
                                        padding: 0,
                                    }}
                                >
                                    {getGroupsForSelectedUsers.map((group, index) => (
                                        <React.Fragment key={group.id}>
                                            {index > 0 && <Divider />}
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    onClick={() => handleGroupToggle(group)}
                                                    selected={selectedGroupsForDelete.some(
                                                        (g) => g.id === group.id
                                                    )}
                                                    sx={{
                                                        '&.Mui-selected': {
                                                            backgroundColor:
                                                                'rgba(196, 36, 49, 0.08)',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(196, 36, 49, 0.12)',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar src={group.logo} alt={group.name} />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={group.name}
                                                        secondary={
                                                            selectedGroupsForDelete.some(
                                                                (g) => g.id === group.id
                                                            )
                                                                ? t('selected_for_deletion')
                                                                : t('click_to_select')
                                                        }
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </>
                        ) : null}

                        {selectedGroupsForDelete.length > 0 && (
                            <Box sx={{ mb: 3, mt: 3 }}>
                                <Typography
                                    fontSize="1.4rem"
                                    color="text.secondary"
                                    paragraph
                                    sx={{
                                        textAlign: 'center',
                                        maxWidth: '500px',
                                        margin: '0 auto',
                                    }}
                                >
                                    {getGroupsForSelectedUsers.length === 1
                                        ? t('delete_from_single_group_description', {
                                              count: selectedUsers.size,
                                              userText:
                                                  selectedUsers.size === 1
                                                      ? t('member_singular_plural')
                                                      : t('member_singular_plural'),
                                              groupName: getGroupsForSelectedUsers[0].name,
                                              defaultValue:
                                                  'Þú ert að fara að fjarlægja {{count}} {{userText}} úr hópnum {{groupName}}.',
                                          })
                                        : t('delete_from_groups_description', {
                                              count: selectedUsers.size,
                                              groupCount: selectedGroupsForDelete.length,
                                              userText:
                                                  selectedUsers.size === 1
                                                      ? t('member_singular')
                                                      : t('member_plural'),
                                              groupText:
                                                  selectedGroupsForDelete.length === 1
                                                      ? t('group_singular')
                                                      : t('group_plural'),
                                              defaultValue:
                                                  'Þú ert að fara að fjarlægja {{count}} {{userText}} úr {{groupCount}} {{groupText}}.',
                                          })}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowDeleteDialog(false)}
                            color="success"
                            variant="contained"
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="danger"
                            variant="contained"
                            onClick={handleDeleteFromGroups}
                            disabled={selectedGroupsForDelete.length === 0}
                        >
                            {t('remove')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                {canUpdate && selectedUser && (
                    <MenuItem onClick={() => handleUpdateGroups(selectedUser)}>
                        <Edit sx={{ mr: 1, color: 'primary.main' }} />
                        {t('update_groups')}
                    </MenuItem>
                )}
                {canUpdate && selectedUser && (
                    <MenuItem onClick={() => handleDeleteUser(selectedUser.id)}>
                        <Delete sx={{ mr: 1, color: 'error.main' }} />
                        {t('remove_all_groups')}
                    </MenuItem>
                )}
            </Menu>
            {/* Innflutningsmodal */}
            <Dialog
                open={isImportModalOpen}
                onClose={() => {
                    // Leyfum ekki að loka ef innflutningur er enn í gangi
                    if (!batchesData || batchesData.length === 0) {
                        return;
                    }
                    setIsImportModalOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        textAlign: 'center',
                        fontWeight: 600,
                        fontStyle: 'italic',
                        color: '#DE2C48',
                        textAlign: 'left',
                    }}
                >
                    {t ? t('importing') : 'Gef notendum aðgang...'}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#DE2C48' }} />
                        <Typography>
                            {t('batch_import_progress', {
                                total: formatWithThousandSeparator(totalBatchJobs),
                                left: formatWithThousandSeparator(
                                    totalBatchJobs - (pendingJobs || totalBatchJobs)
                                ),
                                percentage: Math.round(importProgress),
                                defaultValue: '{{left}}/{{total}} ({{percentage}}%)',
                            })}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={
                            ((totalBatchJobs - (pendingJobs || totalBatchJobs)) /
                                (totalBatchJobs || 1)) *
                            100
                        }
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: '#F8D5DA',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#DE2C48',
                            },
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 2,
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            fontStyle: 'italic',
                        }}
                    >
                        {t('import_background_message', {
                            defaultValue:
                                'Þú getur lokað þessum glugga. Innflutningurinn heldur áfram í bakgrunni.',
                        })}
                    </Typography>
                </DialogContent>
                {/* Ef villa eða búið, birta hnapp til að loka */}
                {importProgress === 100 && (
                    <DialogActions>
                        <Button onClick={() => setIsImportModalOpen(false)} color="primary">
                            {t('close', { defaultValue: 'Loka' })}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.my_employees')}
                    rightComponent={
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {showMembershipPhotos && (
                                <SparaButton
                                    color="primary"
                                    onClick={() => setBulkPhotoOpen(true)}
                                >
                                    {t('membership_photo_bulk')}
                                </SparaButton>
                            )}
                            <SparaButton color="primary" onClick={htmlAlert}>
                                {addSpecialUserLabel}
                            </SparaButton>
                        </Box>
                    }
                />
                <Card style={{ marginTop: '0px' }}>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: '50px',
                            backgroundColor: 'var(--spara-chrome, white)',
                            zIndex: 99,
                            padding: '8px 8px 0px 16px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            marginBottom: '0',
                        }}
                    >
                        <SparaFilter
                            filters={filters}
                            onFilterClick={handleFilterClick}
                            onClearAll={handleClearAll}
                            search={search}
                            onSearchChange={handleSearchInputChange}
                            filterGroups={filterGroups}
                            t={t}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 1,
                                px: 1,
                            }}
                        >
                            <Fade in={isActiveLoadMore} timeout={300}>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        fontStyle: 'italic',
                                        color: 'var(--spara-offer-well-text, #007aaa)',
                                        transition: 'all 0.3s ease-in-out',
                                    }}
                                >
                                    {`${t('filter.showing')} ${formatWithThousandSeparator(
                                        allPages.length
                                    )} ${t('filter.of')} ${formatWithThousandSeparator(
                                        totalCount
                                    )} ${t('filter.members')}`}
                                </div>
                            </Fade>

                            <Fade in={selectedUsers.size > 0} timeout={400}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1,
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Slide direction="up" in={selectedUsers.size > 0} timeout={400}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    fontStyle: 'italic',
                                                    color: '#999',
                                                    transition: 'all 0.3s ease-in-out',
                                                }}
                                            >
                                                {t('selected_items_count', {
                                                    count: selectedUsers.size,
                                                    userText:
                                                        selectedUsers.size === 1
                                                            ? t('member_singular')
                                                            : t('member_plural'),
                                                    selectedText:
                                                        selectedUsers.size === 1
                                                            ? t('selected_singular')
                                                            : t('selected_plural'),
                                                    defaultValue:
                                                        '{{count}} {{userText}} {{selectedText}}',
                                                })}
                                            </Typography>
                                            <SparaButton
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    minWidth: 'auto',
                                                    px: 1,
                                                    py: 0.5,
                                                    fontSize: '0.75rem',
                                                    transition:
                                                        'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                }}
                                                onClick={() => {
                                                    setSelectedUsers(new Set());
                                                    setSelectAll(false);
                                                }}
                                            >
                                                {t('clear_selection')}
                                            </SparaButton>
                                            <SparaButton
                                                onClick={() => setShowDeleteDialog(true)}
                                                size="small"
                                                sx={{
                                                    minWidth: 'auto',
                                                    px: 1,
                                                    py: 0.5,
                                                    fontSize: '0.75rem',
                                                    transition:
                                                        'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                    },
                                                }}
                                            >
                                                {t(
                                                    filterGroups?.[0]?.length === 1
                                                        ? 'delete_from_group'
                                                        : 'delete_from_groups'
                                                )}
                                            </SparaButton>
                                        </Box>
                                    </Slide>
                                </Box>
                            </Fade>
                        </Box>
                    </Box>

                    <Fade in={isLoadingUsers || isLoadingCompanies || isMutating} timeout={300}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease-in-out',
                            }}
                        >
                            {(isLoadingUsers || isLoadingCompanies || isMutating) && page === 1 && (
                                <CircularProgress
                                    sx={{
                                        transition: 'all 0.3s ease-in-out',
                                    }}
                                />
                            )}
                        </Box>
                    </Fade>

                    {!isLoadingUsers &&
                        !isLoadingCompanies &&
                        !isMutating &&
                        !isFetching &&
                        specialUsersData?.results?.length === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    px: 2,
                                    textAlign: 'center',
                                }}
                            >
                                {debouncedSearchValue ? (
                                    <>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 2,
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            {t('no_search_results')}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 3,
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            {t('no_search_results_description', {
                                                action: addSpecialUserLabel,
                                            })}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 2,
                                                fontSize: '1.1rem',
                                            }}
                                        >
                                            {filterGroups?.[0]?.length === 1
                                                ? t('no_members_in_single_group', {
                                                      groupName: filterGroups[0][0].label,
                                                  })
                                                : t('no_members_in_groups', {
                                                      groupNames: filterGroups?.[0]
                                                          ?.map((group) => group.label)
                                                          .join('" "'),
                                                  })}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 3,
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            {t('add_members_description', {
                                                action: addSpecialUserLabel,
                                            })}
                                        </Typography>
                                    </>
                                )}
                                <SparaButton
                                    onClick={htmlAlert}
                                    color="primary"
                                    sx={{
                                        minWidth: '200px',
                                    }}
                                >
                                    {addSpecialUserLabel}
                                </SparaButton>
                            </Box>
                        )}

                    {allPages.length > 0 && (
                        <>
                            <Table
                                striped
                                tableHead={getTableHead()}
                                tableData={getConnectedUsersData()}
                                customCellClasses={Array(getTableHead().length).fill(classes.left)}
                                customClassesForCells={Array(getTableHead().length)
                                    .fill(0)
                                    .map((_, i) => i)}
                                customHeadCellClasses={Array(getTableHead().length).fill(
                                    classes.left
                                )}
                                customHeadClassesForCells={Array(getTableHead().length)
                                    .fill(0)
                                    .map((_, i) => i)}
                                tableProps={{
                                    sx: {
                                        '& .MuiTableCell-root': {
                                            padding: '0 !important',
                                            height: 'auto',
                                            minHeight: '42px',
                                        },
                                        '& .MuiTableCell-head': {
                                            padding: '4px 8px !important',
                                            height: '32px',
                                            minHeight: '32px',
                                            '& .MuiTypography-root': {
                                                fontSize: '0.8125rem',
                                                lineHeight: 1,
                                            },
                                        },
                                        '& td': {
                                            padding: '0 !important',
                                            height: 'auto',
                                            '&.MuiTableCell-root': {
                                                padding: '0 !important',
                                                height: 'auto',
                                            },
                                            '&.MuiTableCell-body': {
                                                padding: '0 !important',
                                                height: 'auto',
                                            },
                                        },
                                        '& th': {
                                            padding: '4px 8px !important',
                                        },
                                        '& .MuiTableCell-body': {
                                            padding: '0 !important',
                                            height: 'auto',
                                        },
                                        '& .MuiTable-root': {
                                            '& td': {
                                                padding: '0 !important',
                                                height: 'auto',
                                            },
                                        },
                                        '& .MuiTableBody-root': {
                                            '& td': {
                                                padding: '0 !important',
                                                height: 'auto',
                                            },
                                        },
                                        '& .MuiTableRow-root': {
                                            height: 'auto',
                                            '& td': {
                                                padding: '0 !important',
                                                height: 'auto',
                                            },
                                        },
                                    },
                                    style: {
                                        '& td': {
                                            padding: '0 !important',
                                            height: 'auto',
                                        },
                                    },
                                }}
                            />
                        </>
                    )}

                    {hasMore && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '20px',
                                borderTop: '1px solid #eee',
                            }}
                        >
                            <SparaButton
                                onClick={handleLoadMore}
                                disabled={
                                    isLoadingMore || isLoadingUsers || isMutating || isFetching
                                }
                                color="primary"
                            >
                                {isLoadingMore ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} color="inherit" />
                                        {t('loading')}...
                                    </Box>
                                ) : (
                                    t('load_more')
                                )}
                            </SparaButton>
                        </Box>
                    )}
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default withTranslation('', { withRef: true })(SpecialUsers);
